// Backend integration point: replace all exports with API calls to your TPRM backend

export type RiskTier = 'Critical' | 'High' | 'Moderate' | 'Low';
export type CriticalityTier = 'Critical' | 'High' | 'Moderate' | 'Low';
export type LifecycleStage =
  | 'Intake' |'Sourcing' |'Due Diligence' |'Contracting' |'Onboarding' |'Active' |'Renewal' |'Terminated';
export type ContractStatus =
  | 'Draft' |'Legal Review' |'Negotiation' |'Pending Signature' |'Active' |'Expiring' |'Terminated' |'Archived';
export type DDStatus =
  | 'Not Started' |'In Progress' |'Pending Approval' |'Complete' |'Overdue' |'Waived';
export type InsuranceStatus = 'Current' | 'Expiring Soon' | 'Expired' | 'Not Required' | 'Missing';
export type IssueSeverity = 'Critical' | 'High' | 'Moderate' | 'Low';
export type IssueStatus =
  | 'New' |'Under Review' |'Remediation' |'Pending Validation' |'Closed' |'Accepted Risk' |'Escalated';

export interface Vendor {
  id: string;
  name: string;
  dba?: string;
  vendorId: string;
  category: string;
  services: string;
  department: string;
  departmentOwner: string;
  contractOwner: string;
  riskTier: RiskTier;
  criticality: CriticalityTier;
  lifecycleStage: LifecycleStage;
  contractStatus: ContractStatus;
  ddStatus: DDStatus;
  insuranceStatus: InsuranceStatus;
  contractStart: string;
  contractEnd: string;
  nextReview: string;
  lastReview: string;
  residualRisk: RiskTier;
  dataHandled: string[];
  systemsAccessed: string[];
  hostingModel: string;
  openIssues: number;
  openCriticalIssues: number;
  overallStatus: 'Good Standing' | 'Needs Attention' | 'At Risk' | 'Non-Compliant';
}

export interface Issue {
  id: string;
  vendorId: string;
  vendorName: string;
  category: string;
  severity: IssueSeverity;
  status: IssueStatus;
  dateIdentified: string;
  dueDate: string;
  description: string;
  assignedOwner: string;
  department: string;
}

export interface DDReview {
  id: string;
  vendorId: string;
  vendorName: string;
  domain: string;
  reviewer: string;
  status: DDStatus;
  dueDate: string;
  riskTier: RiskTier;
  findingsCount: number;
  completionPct: number;
}

export interface ActivityItem {
  id: string;
  timestamp: string;
  user: string;
  role: string;
  action: string;
  entity: string;
  entityId: string;
  detail: string;
}

export const VENDORS: Vendor[] = [
  {
    id: 'vendor-001',
    name: 'Accela Government Solutions',
    dba: 'Accela',
    vendorId: 'VND-2024-001',
    category: 'SaaS Platform',
    services: 'Permitting & Licensing Platform',
    department: 'Community Development',
    departmentOwner: 'Diane Harrington',
    contractOwner: 'Marcus Webb',
    riskTier: 'Critical',
    criticality: 'Critical',
    lifecycleStage: 'Active',
    contractStatus: 'Active',
    ddStatus: 'Complete',
    insuranceStatus: 'Current',
    contractStart: '2023-07-01',
    contractEnd: '2026-06-30',
    nextReview: '2026-04-15',
    lastReview: '2025-04-10',
    residualRisk: 'Moderate',
    dataHandled: ['Resident PII', 'Business Records', 'Payment Data'],
    systemsAccessed: ['Permitting DB', 'GIS', 'Payment Gateway'],
    hostingModel: 'SaaS',
    openIssues: 2,
    openCriticalIssues: 0,
    overallStatus: 'Good Standing',
  },
  {
    id: 'vendor-002',
    name: 'NorthStar Utility Billing Inc.',
    vendorId: 'VND-2024-002',
    category: 'Billing Processor',
    services: 'Utility Billing & Payment Processing',
    department: 'Public Works / Utilities',
    departmentOwner: 'Raj Patel',
    contractOwner: 'Sandra Chu',
    riskTier: 'Critical',
    criticality: 'Critical',
    lifecycleStage: 'Active',
    contractStatus: 'Expiring',
    ddStatus: 'In Progress',
    insuranceStatus: 'Expiring Soon',
    contractStart: '2021-01-01',
    contractEnd: '2026-04-30',
    nextReview: '2026-03-30',
    lastReview: '2024-10-05',
    residualRisk: 'High',
    dataHandled: ['Resident PII', 'Payment Data', 'Financial Records'],
    systemsAccessed: ['Utility Billing System', 'ACH Gateway', 'Customer Portal'],
    hostingModel: 'Managed Service',
    openIssues: 4,
    openCriticalIssues: 1,
    overallStatus: 'At Risk',
  },
  {
    id: 'vendor-003',
    name: 'Presidio Managed Services LLC',
    vendorId: 'VND-2023-003',
    category: 'IT Services',
    services: 'Managed IT Infrastructure & Help Desk',
    department: 'Information Technology',
    departmentOwner: 'Kevin Tanaka',
    contractOwner: 'Marcus Webb',
    riskTier: 'High',
    criticality: 'Critical',
    lifecycleStage: 'Active',
    contractStatus: 'Active',
    ddStatus: 'Complete',
    insuranceStatus: 'Current',
    contractStart: '2022-04-01',
    contractEnd: '2027-03-31',
    nextReview: '2026-09-01',
    lastReview: '2025-09-15',
    residualRisk: 'Moderate',
    dataHandled: ['Employee Data', 'System Logs', 'Network Data'],
    systemsAccessed: ['City Network', 'AD/Identity', 'Email', 'Endpoints'],
    hostingModel: 'On-Premises',
    openIssues: 1,
    openCriticalIssues: 0,
    overallStatus: 'Good Standing',
  },
  {
    id: 'vendor-004',
    name: 'Kleinfelder Group Inc.',
    vendorId: 'VND-2024-004',
    category: 'Engineering Consultant',
    services: 'Civil Engineering & Infrastructure Design',
    department: 'Public Works',
    departmentOwner: 'Tomás Rivera',
    contractOwner: 'Sandra Chu',
    riskTier: 'Moderate',
    criticality: 'High',
    lifecycleStage: 'Active',
    contractStatus: 'Active',
    ddStatus: 'Complete',
    insuranceStatus: 'Current',
    contractStart: '2024-03-01',
    contractEnd: '2026-08-31',
    nextReview: '2026-08-01',
    lastReview: '2024-03-15',
    residualRisk: 'Low',
    dataHandled: ['Project Plans', 'Infrastructure Data'],
    systemsAccessed: ['GIS', 'Project Management Portal'],
    hostingModel: 'Consultant',
    openIssues: 0,
    openCriticalIssues: 0,
    overallStatus: 'Good Standing',
  },
  {
    id: 'vendor-005',
    name: 'ADP TotalSource',
    vendorId: 'VND-2022-005',
    category: 'Payroll / HR',
    services: 'Payroll Processing & HR Administration',
    department: 'Human Resources',
    departmentOwner: 'Angela Moreno',
    contractOwner: 'Marcus Webb',
    riskTier: 'Critical',
    criticality: 'Critical',
    lifecycleStage: 'Active',
    contractStatus: 'Active',
    ddStatus: 'Complete',
    insuranceStatus: 'Current',
    contractStart: '2022-01-01',
    contractEnd: '2027-12-31',
    nextReview: '2026-06-30',
    lastReview: '2025-01-10',
    residualRisk: 'Moderate',
    dataHandled: ['Employee PII', 'Payroll Data', 'Tax Records', 'Banking Info'],
    systemsAccessed: ['HRIS', 'Payroll System', 'Benefits Portal'],
    hostingModel: 'SaaS',
    openIssues: 0,
    openCriticalIssues: 0,
    overallStatus: 'Good Standing',
  },
  {
    id: 'vendor-006',
    name: 'Iron Mountain Federal Services',
    vendorId: 'VND-2023-006',
    category: 'Records Management',
    services: 'Physical Records Storage & Destruction',
    department: 'City Clerk',
    departmentOwner: 'Patricia Nguyen',
    contractOwner: 'Sandra Chu',
    riskTier: 'Moderate',
    criticality: 'Moderate',
    lifecycleStage: 'Active',
    contractStatus: 'Active',
    ddStatus: 'Complete',
    insuranceStatus: 'Current',
    contractStart: '2023-06-01',
    contractEnd: '2026-05-31',
    nextReview: '2026-05-01',
    lastReview: '2024-06-01',
    residualRisk: 'Low',
    dataHandled: ['City Records', 'Legal Documents', 'Personnel Files'],
    systemsAccessed: [],
    hostingModel: 'On-Premises',
    openIssues: 1,
    openCriticalIssues: 0,
    overallStatus: 'Good Standing',
  },
  {
    id: 'vendor-007',
    name: 'Granicus LLC',
    vendorId: 'VND-2024-007',
    category: 'SaaS Platform',
    services: 'City Website Hosting & Digital Engagement',
    department: 'City Manager\'s Office',
    departmentOwner: 'James Okafor',
    contractOwner: 'Marcus Webb',
    riskTier: 'High',
    criticality: 'High',
    lifecycleStage: 'Active',
    contractStatus: 'Active',
    ddStatus: 'Overdue',
    insuranceStatus: 'Current',
    contractStart: '2024-01-01',
    contractEnd: '2026-12-31',
    nextReview: '2026-01-15',
    lastReview: '2024-01-20',
    residualRisk: 'Moderate',
    dataHandled: ['Public Records', 'Resident Contact Info'],
    systemsAccessed: ['CMS', 'Meeting Management', 'Notification System'],
    hostingModel: 'SaaS',
    openIssues: 3,
    openCriticalIssues: 1,
    overallStatus: 'Needs Attention',
  },
  {
    id: 'vendor-008',
    name: 'Staples Business Advantage',
    vendorId: 'VND-2021-008',
    category: 'Commodity Supplier',
    services: 'Office Supplies & Equipment',
    department: 'Administration',
    departmentOwner: 'Linda Park',
    contractOwner: 'Sandra Chu',
    riskTier: 'Low',
    criticality: 'Low',
    lifecycleStage: 'Active',
    contractStatus: 'Expiring',
    ddStatus: 'Waived',
    insuranceStatus: 'Current',
    contractStart: '2021-09-01',
    contractEnd: '2026-04-15',
    nextReview: '2026-04-01',
    lastReview: '2023-09-01',
    residualRisk: 'Low',
    dataHandled: [],
    systemsAccessed: ['Procurement Portal'],
    hostingModel: 'Commodity',
    openIssues: 0,
    openCriticalIssues: 0,
    overallStatus: 'Good Standing',
  },
  {
    id: 'vendor-009',
    name: 'Iteris Inc.',
    vendorId: 'VND-2023-009',
    category: 'Infrastructure Tech',
    services: 'Intelligent Traffic Systems Integration',
    department: 'Public Works',
    departmentOwner: 'Tomás Rivera',
    contractOwner: 'Sandra Chu',
    riskTier: 'High',
    criticality: 'Critical',
    lifecycleStage: 'Onboarding',
    contractStatus: 'Active',
    ddStatus: 'In Progress',
    insuranceStatus: 'Missing',
    contractStart: '2026-01-15',
    contractEnd: '2029-01-14',
    nextReview: '2026-07-15',
    lastReview: '',
    residualRisk: 'High',
    dataHandled: ['Traffic Data', 'Infrastructure Data'],
    systemsAccessed: ['Traffic Management System', 'City SCADA Network'],
    hostingModel: 'On-Premises',
    openIssues: 2,
    openCriticalIssues: 1,
    overallStatus: 'At Risk',
  },
  {
    id: 'vendor-010',
    name: 'Tyler Technologies — Munis',
    vendorId: 'VND-2020-010',
    category: 'ERP / Finance',
    services: 'Financial Management & ERP',
    department: 'Finance',
    departmentOwner: 'Robert Kim',
    contractOwner: 'Marcus Webb',
    riskTier: 'Critical',
    criticality: 'Critical',
    lifecycleStage: 'Renewal',
    contractStatus: 'Expiring',
    ddStatus: 'Pending Approval',
    insuranceStatus: 'Current',
    contractStart: '2020-07-01',
    contractEnd: '2026-06-30',
    nextReview: '2026-04-30',
    lastReview: '2025-03-01',
    residualRisk: 'Moderate',
    dataHandled: ['Financial Records', 'Budget Data', 'Vendor Payment Data', 'Employee Payroll'],
    systemsAccessed: ['ERP', 'GL', 'AP/AR', 'Budgeting'],
    hostingModel: 'SaaS',
    openIssues: 1,
    openCriticalIssues: 0,
    overallStatus: 'Needs Attention',
  },
  {
    id: 'vendor-011',
    name: 'Civic Plus — CivicEngage',
    vendorId: 'VND-2025-011',
    category: 'SaaS Platform',
    services: 'Resident Engagement & Service Requests',
    department: 'City Manager\'s Office',
    departmentOwner: 'James Okafor',
    contractOwner: 'Marcus Webb',
    riskTier: 'High',
    criticality: 'High',
    lifecycleStage: 'Due Diligence',
    contractStatus: 'Pending Signature',
    ddStatus: 'In Progress',
    insuranceStatus: 'Not Required',
    contractStart: '',
    contractEnd: '',
    nextReview: '2026-06-01',
    lastReview: '',
    residualRisk: 'High',
    dataHandled: ['Resident PII', 'Service Request Data'],
    systemsAccessed: ['Service Portal', 'CRM'],
    hostingModel: 'SaaS',
    openIssues: 0,
    openCriticalIssues: 0,
    overallStatus: 'Needs Attention',
  },
  {
    id: 'vendor-012',
    name: 'First Data Government Solutions',
    vendorId: 'VND-2022-012',
    category: 'Payment Processor',
    services: 'Credit/Debit Payment Processing',
    department: 'Finance',
    departmentOwner: 'Robert Kim',
    contractOwner: 'Sandra Chu',
    riskTier: 'Critical',
    criticality: 'Critical',
    lifecycleStage: 'Active',
    contractStatus: 'Active',
    ddStatus: 'Complete',
    insuranceStatus: 'Current',
    contractStart: '2022-03-01',
    contractEnd: '2027-02-28',
    nextReview: '2026-08-01',
    lastReview: '2025-08-05',
    residualRisk: 'Moderate',
    dataHandled: ['Payment Card Data', 'Financial Records', 'Resident PII'],
    systemsAccessed: ['Payment Gateway', 'POS Systems', 'Utility Billing'],
    hostingModel: 'Managed Service',
    openIssues: 0,
    openCriticalIssues: 0,
    overallStatus: 'Good Standing',
  },
];

export const ISSUES: Issue[] = [
  {
    id: 'ISS-2026-001',
    vendorId: 'vendor-002',
    vendorName: 'NorthStar Utility Billing Inc.',
    category: 'Cybersecurity',
    severity: 'Critical',
    status: 'Remediation',
    dateIdentified: '2026-02-14',
    dueDate: '2026-04-14',
    description: 'MFA not enforced on admin accounts accessing city billing data.',
    assignedOwner: 'Kevin Tanaka',
    department: 'Information Technology',
  },
  {
    id: 'ISS-2026-002',
    vendorId: 'vendor-007',
    vendorName: 'Granicus LLC',
    category: 'Security Assessment',
    severity: 'Critical',
    status: 'Under Review',
    dateIdentified: '2026-03-01',
    dueDate: '2026-04-30',
    description: 'Annual SOC 2 report not provided; last assessment expired Dec 2025.',
    assignedOwner: 'Kevin Tanaka',
    department: 'Information Technology',
  },
  {
    id: 'ISS-2026-003',
    vendorId: 'vendor-009',
    vendorName: 'Iteris Inc.',
    category: 'Insurance',
    severity: 'Critical',
    status: 'New',
    dateIdentified: '2026-03-20',
    dueDate: '2026-04-05',
    description: 'Certificate of insurance not received 60+ days post-contract execution.',
    assignedOwner: 'Sandra Chu',
    department: 'Procurement',
  },
  {
    id: 'ISS-2026-004',
    vendorId: 'vendor-002',
    vendorName: 'NorthStar Utility Billing Inc.',
    category: 'Privacy',
    severity: 'High',
    status: 'Remediation',
    dateIdentified: '2026-02-20',
    dueDate: '2026-05-01',
    description: 'Data retention schedule not aligned with city records retention policy.',
    assignedOwner: 'Patricia Nguyen',
    department: 'City Clerk',
  },
  {
    id: 'ISS-2026-005',
    vendorId: 'vendor-007',
    vendorName: 'Granicus LLC',
    category: 'SLA Performance',
    severity: 'High',
    status: 'Pending Validation',
    dateIdentified: '2026-01-15',
    dueDate: '2026-03-31',
    description: 'Uptime SLA missed 3 consecutive months (99.5% required, 98.1% achieved).',
    assignedOwner: 'James Okafor',
    department: "City Manager's Office",
  },
  {
    id: 'ISS-2026-006',
    vendorId: 'vendor-010',
    vendorName: 'Tyler Technologies — Munis',
    category: 'Patch Management',
    severity: 'Moderate',
    status: 'Remediation',
    dateIdentified: '2026-03-10',
    dueDate: '2026-05-10',
    description: 'Critical security patches not applied within 30-day SLA for 2 modules.',
    assignedOwner: 'Kevin Tanaka',
    department: 'Information Technology',
  },
];

export const DD_REVIEWS: DDReview[] = [
  {
    id: 'DD-2026-001',
    vendorId: 'vendor-002',
    vendorName: 'NorthStar Utility Billing Inc.',
    domain: 'Cybersecurity',
    reviewer: 'Kevin Tanaka',
    status: 'In Progress',
    dueDate: '2026-04-30',
    riskTier: 'Critical',
    findingsCount: 3,
    completionPct: 60,
  },
  {
    id: 'DD-2026-002',
    vendorId: 'vendor-002',
    vendorName: 'NorthStar Utility Billing Inc.',
    domain: 'Privacy',
    reviewer: 'Patricia Nguyen',
    status: 'In Progress',
    dueDate: '2026-04-30',
    riskTier: 'Critical',
    findingsCount: 2,
    completionPct: 75,
  },
  {
    id: 'DD-2026-003',
    vendorId: 'vendor-009',
    vendorName: 'Iteris Inc.',
    domain: 'Cybersecurity',
    reviewer: 'Kevin Tanaka',
    status: 'In Progress',
    dueDate: '2026-05-15',
    riskTier: 'High',
    findingsCount: 1,
    completionPct: 40,
  },
  {
    id: 'DD-2026-004',
    vendorId: 'vendor-009',
    vendorName: 'Iteris Inc.',
    domain: 'Insurance',
    reviewer: 'Sandra Chu',
    status: 'In Progress',
    dueDate: '2026-04-15',
    riskTier: 'High',
    findingsCount: 1,
    completionPct: 20,
  },
  {
    id: 'DD-2026-005',
    vendorId: 'vendor-011',
    vendorName: 'Civic Plus — CivicEngage',
    domain: 'Cybersecurity',
    reviewer: 'Kevin Tanaka',
    status: 'In Progress',
    dueDate: '2026-05-01',
    riskTier: 'High',
    findingsCount: 0,
    completionPct: 55,
  },
  {
    id: 'DD-2026-006',
    vendorId: 'vendor-011',
    vendorName: 'Civic Plus — CivicEngage',
    domain: 'Privacy',
    reviewer: 'Patricia Nguyen',
    status: 'Not Started',
    dueDate: '2026-05-01',
    riskTier: 'High',
    findingsCount: 0,
    completionPct: 0,
  },
  {
    id: 'DD-2026-007',
    vendorId: 'vendor-010',
    vendorName: 'Tyler Technologies — Munis',
    domain: 'Financial',
    reviewer: 'Robert Kim',
    status: 'Pending Approval',
    dueDate: '2026-04-15',
    riskTier: 'Critical',
    findingsCount: 1,
    completionPct: 100,
  },
  {
    id: 'DD-2026-008',
    vendorId: 'vendor-007',
    vendorName: 'Granicus LLC',
    domain: 'Security Assessment',
    reviewer: 'Kevin Tanaka',
    status: 'Overdue',
    dueDate: '2026-02-28',
    riskTier: 'High',
    findingsCount: 2,
    completionPct: 30,
  },
];

export const ACTIVITY_LOG: ActivityItem[] = [
  {
    id: 'act-001',
    timestamp: '2026-03-28 16:42',
    user: 'Kevin Tanaka',
    role: 'IT / Security',
    action: 'Finding Added',
    entity: 'Due Diligence',
    entityId: 'DD-2026-001',
    detail: 'Added critical finding: MFA not enforced on NorthStar admin accounts.',
  },
  {
    id: 'act-002',
    timestamp: '2026-03-28 14:15',
    user: 'Sandra Chu',
    role: 'Procurement',
    action: 'Issue Opened',
    entity: 'Issue',
    entityId: 'ISS-2026-003',
    detail: 'Opened insurance lapse issue for Iteris Inc. — COI not received.',
  },
  {
    id: 'act-003',
    timestamp: '2026-03-27 11:30',
    user: 'Patricia Nguyen',
    role: 'Privacy / Records',
    action: 'Review Assigned',
    entity: 'Due Diligence',
    entityId: 'DD-2026-006',
    detail: 'Assigned privacy review for Civic Plus to self. Due 05/01/2026.',
  },
  {
    id: 'act-004',
    timestamp: '2026-03-26 09:05',
    user: 'Marcus Webb',
    role: 'Procurement',
    action: 'Contract Renewal',
    entity: 'Contract',
    entityId: 'VND-2020-010',
    detail: 'Tyler Technologies Munis renewal initiated. Current term ends 06/30/2026.',
  },
  {
    id: 'act-005',
    timestamp: '2026-03-25 15:48',
    user: 'James Okafor',
    role: 'Dept. Owner',
    action: 'Issue Escalated',
    entity: 'Issue',
    entityId: 'ISS-2026-005',
    detail: 'Granicus SLA issue escalated to City Manager for review.',
  },
  {
    id: 'act-006',
    timestamp: '2026-03-24 13:20',
    user: 'Robert Kim',
    role: 'Finance',
    action: 'DD Approved',
    entity: 'Due Diligence',
    entityId: 'DD-2026-007',
    detail: 'Approved financial review for Tyler Technologies. One finding accepted.',
  },
];

export const INTAKE_TREND = [
  { month: 'Oct', submitted: 3, approved: 2, rejected: 0 },
  { month: 'Nov', submitted: 5, approved: 3, rejected: 1 },
  { month: 'Dec', submitted: 2, approved: 2, rejected: 0 },
  { month: 'Jan', submitted: 4, approved: 3, rejected: 0 },
  { month: 'Feb', submitted: 6, approved: 4, rejected: 1 },
  { month: 'Mar', submitted: 4, approved: 2, rejected: 0 },
];

export const DEPT_RISK_DATA = [
  { dept: 'Finance', critical: 2, high: 1, moderate: 0, low: 0 },
  { dept: 'Public Works', critical: 1, high: 2, moderate: 1, low: 0 },
  { dept: 'IT', critical: 1, high: 1, moderate: 0, low: 0 },
  { dept: 'Community Dev', critical: 1, high: 0, moderate: 0, low: 0 },
  { dept: 'HR', critical: 1, high: 0, moderate: 0, low: 0 },
  { dept: 'City Mgr', critical: 0, high: 2, moderate: 0, low: 0 },
  { dept: 'City Clerk', critical: 0, high: 0, moderate: 1, low: 0 },
  { dept: 'Admin', critical: 0, high: 0, moderate: 0, low: 1 },
];

export const LIFECYCLE_FUNNEL = [
  { stage: 'Active', count: 9, color: '#0d9488' },
  { stage: 'Onboarding', count: 1, color: '#0891b2' },
  { stage: 'Due Diligence', count: 1, color: '#0284c7' },
  { stage: 'Renewal', count: 1, color: '#d97706' },
  { stage: 'Intake', count: 0, color: '#6b7280' },
];